<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        md="5"
      >
        <v-text-field
          v-model="maps.searchAddressInput"
          :label="$t(&quot;Enter a location&quot;)"
          persistent-hint
          outlined
          dense
          full-width
          @keyup="search"
          @keyup.enter="searchLocation()"
        ></v-text-field>
        <ul
          v-if="maps.places.length > 0"
          role="menu"
          class="places-dropdown"
        >
          <li><strong>{{ $t("Suggestions") }}</strong></li>
          <li
            v-for="place in maps.places"
            :key="place.PlaceId"
            role="menuitem"
            class="place"
            @click="selectPlace(place)"
          >
            {{ place.Text }}
          </li>
        </ul>
      </v-col>
      <v-col
        cols="12"
        md="2"
      >
        <v-btn
          color="primary"
          @click="searchLocation()"
        >
          {{ $t("\n          Search\n        ") }}
        </v-btn>
        <v-tooltip
          v-if="!$vuetify.breakpoint.mdAndDown"
          top
          color="#000000"
          max-width="200px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              :class="{'alternate-icon-small': !mdiInformationOutline}"
              class="mx-auto ml-2"
              :title="$t(&quot;What's a tag?&quot;)"
              v-bind="attrs"
              v-on="on"
            >
              {{ mdiInformationOutline }}
            </v-icon>
          </template>
          <span>{{ $t("Activate your ") }}<strong>{{ $t("current location") }}</strong>{{ $t(" or ") }}<strong>{{ $t("Search by Address") }}</strong>{{ $t(" to link this content to a location or click on the map to set manually.") }}<br />{{ $t("\n            You can drag the marker to change the location.") }}</span>
        </v-tooltip>
      </v-col>
      <v-col
        cols="12"
        md="5"
        class="location-actions"
      >
        <v-btn
          color="primary"
          outlined
          :loading="locationLoading"
          :disabled="loading || locationErrorCode === 'unsupported' || locationErrorCode === 'insecure'"
          @click="geolocation"
        >
          {{ locationErrorCode === 1 ? $t("Request location access") : (locationError ? $t("Try location again") : $t("Use my location")) }}
        </v-btn>
        <v-btn
          text
          small
          color="primary"
          :aria-expanded="showLocationHelp ? 'true' : 'false'"
          aria-controls="location-permission-help"
          @click="showLocationHelp = !showLocationHelp"
        >
          {{ $t("\n          Location permissions\n        ") }}
        </v-btn>
      </v-col>
    </v-row>

    <v-alert
      v-if="locationError"
      type="warning"
      outlined
      dense
      role="alert"
    >
      {{ locationError }}{{ $t(" You can also search for an address or drag the map marker.\n    ") }}
    </v-alert>
    <v-alert
      v-if="showLocationHelp"
      id="location-permission-help"
      type="info"
      outlined
      class="location-help"
    >
      <strong>{{ $t("Allow location access") }}</strong>
      <p>{{ $t("Tap the location button above and choose Allow if your browser asks. This website cannot change a blocked permission for you.") }}</p>
      <p><strong>{{ $t("iPhone / Safari:") }}</strong>{{ $t(" open the page menu beside the address bar, then Website Settings → Location → Ask or Allow. In iPhone Settings → Privacy & Security → Location Services, enable location access for Safari Websites.") }}</p>
      <p><strong>{{ $t("Other browsers:") }}</strong>{{ $t(" open the site permissions beside the address bar and allow Location. Also check that location services are enabled on your device.") }}</p>
      <p>{{ $t("After changing permissions, try the location button again. If necessary, reload the page after saving your entry to avoid losing your changes.") }}</p>
    </v-alert>

    <v-row>
      <v-col
        cols="12"
        md="12"
      >
        <div class="mapPane">
          <v-progress-circular
            v-if="loading || locationLoading"
            :size="70"
            :width="7"
            color="primary"
            indeterminate
            class="progress-map"
          ></v-progress-circular>
          <div id="map"></div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import maplibregl from 'maplibre-gl'
import { Auth, Signer } from 'aws-amplify'
import Location from 'aws-sdk/clients/location'
import { mdiInformationOutline } from '@mdi/js'
import awsconfig from '../aws-exports'

let map

export default {
  name: 'MapPane',
  props: ['coords'],
  data() {
    return {
      mdiInformationOutline,
      credentials: null,
      client: null,
      params: null,
      marker: null,
      position: {},
      locationLoading: false,
      locationError: '',
      locationErrorCode: null,
      locationRequestId: 0,
      showLocationHelp: false,
      loading: true,
      maps: {
        center: { lat: 0, lng: 0 },
        markers: false,
        zoom: 10,
        searchAddressInput: null,
        places: [],
      },
    }
  },
  beforeMount() {
    this.position = this.coords
  },
  async mounted() {
    this.credentials = await Auth.currentCredentials()
    this.mapCreate()

    if (this.position.lat !== 0 || this.position.lng !== 0) {
      this.reverseGeocode(this.position.lat, this.position.lng)
    }
  },
  beforeDestroy() {
    this.cancelLocationRequest()
  },
  methods: {
    geolocation() {
      if (this.locationLoading) return
      this.locationError = ''
      this.locationErrorCode = null
      if (!window.isSecureContext) {
        this.locationFailed('insecure')

        return
      }
      if (!navigator.geolocation) {
        this.locationFailed('unsupported')

        return
      }
      this.locationLoading = true
      this.locationRequestId += 1
      const requestId = this.locationRequestId
      try {
        // Request directly from the button so Safari can show its permission prompt.
        navigator.geolocation.getCurrentPosition(position => {
          if (requestId !== this.locationRequestId) return
          this.locationLoading = false
          this.showLocationHelp = false
          this.position = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          }
          this.maps.searchAddressInput = ''
          this.addMarker()
        }, error => {
          if (requestId !== this.locationRequestId) return
          this.locationFailed(error.code)
        }, { timeout: 15000, maximumAge: 60000, enableHighAccuracy: false })
      } catch (error) {
        this.locationFailed(error.name === 'SecurityError' ? 1 : 2)
      }
    },
    locationFailed(code) {
      this.locationLoading = false
      this.locationErrorCode = code
      const messages = {
        1: 'Location access is blocked. Check your browser and device permissions below, then try again.',
        2: 'Your current location is unavailable. Check that location services are enabled and try again.',
        3: 'Finding your location took too long. Please try again.',
        insecure: 'Location access requires a secure connection. Open this site using HTTPS.',
        unsupported: 'This browser does not support current location. Try opening this page in Safari or Chrome.',
      }
      this.locationError = messages[code] || messages[2]
      if (code === 1) this.showLocationHelp = true
    },
    cancelLocationRequest() {
      this.locationRequestId += 1
      this.locationLoading = false
      this.locationError = ''
      this.locationErrorCode = null
    },
    async search() {
      if (this.maps.searchAddressInput.length) this.suggestSearch(this.maps.searchAddressInput)
    },
    mapCreate() {
      map = new maplibregl.Map({
        container: 'map',
        style: 'SelfBiography-staging',
        center: [this.position.lng, this.position.lat],
        zoom: 4,
        transformRequest: this.transformRequest,
      })
      const self = this
      map.on('load', () => {
        self.loading = false
      })

      map.addControl(new maplibregl.NavigationControl())
      map.addControl(new maplibregl.FullscreenControl())
      this.addMarker()
    },
    transformRequest(url, resourceType) {
      if (resourceType === 'Style' && !url.includes('://')) {
        url = `https://maps.geo.${awsconfig.aws_project_region}.amazonaws.com/maps/v0/maps/${url}/style-descriptor`
      }
      if (url.includes('amazonaws.com')) {
        return {
          url: Signer.signUrl(url, {
            access_key: this.credentials.accessKeyId,
            secret_key: this.credentials.secretAccessKey,
            session_token: this.credentials.sessionToken,
          }),
        }
      }

      return { url }
    },
    addMarker() {
      if (this.marker) this.marker.remove()
      this.marker = new maplibregl.Marker({
        color: '#9155fd',
        draggable: true,
      })
        .setLngLat([this.position.lng, this.position.lat])

      // .setPopup(popup);
      this.marker.addTo(map)
      this.marker.on('dragstart', this.onDragStart)
      this.marker.on('dragend', this.onDragEnd)
      map.flyTo({
        center: [this.position.lng, this.position.lat],
      })
      this.$emit('updateCoordinates', this.position)
      this.maps.places = []
    },
    removeMarker() {
      this.marker.remove()
    },
    onDragStart() {
      this.maps.searchAddressInput = ''
      this.cancelLocationRequest()
      this.maps.places = []
    },
    onDragEnd() {
      const lngLat = this.marker.getLngLat()
      this.position = {
        lat: lngLat.lat,
        lng: lngLat.lng,
      }
      this.$emit('updateCoordinates', this.position)
    },
    async suggestSearch(term) {
      const location = new Location({
        credentials: this.credentials,
        region: awsconfig.aws_project_region,
      })
      const results = await location.searchPlaceIndexForSuggestions({
        IndexName: 'SelfBiographySearch-staging', // required
        Text: term, // required
      }).promise()
      this.maps.places = results.Results
    },
    searchLocation() {
      this.cancelLocationRequest()
      this.client = new Location({
        credentials: this.credentials,
        region: awsconfig.aws_project_region,
      })
      this.params = {
        IndexName: 'SelfBiographySearch-staging',
        MaxResults: 1,
        Text: this.maps.searchAddressInput,
      }

      this.client.searchPlaceIndexForText(this.params, (err, data) => {
        const label = data.Results[0].Place.Label
        const lng = data.Results[0].Place.Geometry.Point[0]
        const lat = data.Results[0].Place.Geometry.Point[1]
        this.position = {
          lat,
          lng,
        }
        this.addMarker()
        this.$emit('updateCoordinates', this.position)
      })
    },
    selectPlace(place) {
      this.maps.searchAddressInput = place.Text
      this.maps.places = []
      this.searchLocation()
    },
    async reverseGeocode(lat, lng) {
      try {
        const location = new Location({
          credentials: this.credentials,
          region: awsconfig.aws_project_region,
        })
        const result = await location.searchPlaceIndexForPosition({
          IndexName: 'SelfBiographySearch-staging',
          Position: [lng, lat],
          MaxResults: 1,
        }).promise()

        if (result.Results && result.Results.length > 0) {
          this.maps.searchAddressInput = result.Results[0].Place.Label
        }
      } catch (e) {
        console.warn('Reverse geocode failed:', e)
      }
    },
  },
}
</script>

<style scoped lang="scss">
  #map {
    z-index: 0;
    height: 60vh;
    max-height: 500px;
  }
  .location-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
  }
  .location-actions .v-btn {
    max-width: 100%;
    height: auto;
    min-height: 36px;
    padding: 8px 12px;
    white-space: normal;
  }
  .location-actions ::v-deep .v-btn__content {
    flex: 1 1 auto;
  }
  .location-help p {
    margin: 8px 0 0;
  }
  .v-messages {
    display: none;
  }

  .places-dropdown {
    position: absolute;
    top: 55px;
    z-index: 1;
    background: #fff;
    width: 100%;
    max-width: 428px;
    padding: 10px;
    border-radius: 5px;
    box-shadow: 0px 0px 3px rgb(0 0 0 / 40%);
    list-style: none;
    max-height: 354px;
    overflow-y: auto;

    li {
      padding: 8px;
      border-bottom: 1px solid #ccc;
      cursor: pointer;
      &:last-child {
        border: 0;
      }
    }
  }

  .progress-map {
    position: absolute;
    top: 53%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }
</style>
